import React, { useEffect, useState } from 'react'
import { FileUploadProps } from '../../../common/types'
import { isValidFile } from '../../../utilities/commonUtilities'
import InfoBox from '../../app/alertBox/infoBox'
import Icons from '../icons'
import DialogModal from '../modal/DialogModal'
import { useSnackbarManager } from '../snackbar'

const FileUpload: React.FC<FileUploadProps> = ({
  name,
  id,
  label,
  fullwidth = true,
  type = 'file',
  disabled = false,
  required = false,
  isMultiple = false,
  errors,
  value,
  onChange,
  supportedFiles,
  sizeLimit,
  buttonLabel,
  supportedExtensions,
  iconName,
  handleDeleteFile,
  needConfirmation,
  setAttachmentName,
  accept = '*',
}) => {
  const getErrors = (err: any) => {
    let errMsg = ''
    if (err.message) {
      errMsg = err?.message
    }
    return errMsg
  }
  const [file, setFile] = useState<any>(value)
  const [deleteModal, setDeleteModal] = useState(false)
  const [item, setItem] = useState<any>([])
  const { enqueueSnackbar } = useSnackbarManager()
  const handleClearFile = (indexToRemove?: number, item?: any) => {
    if (isMultiple) {
      const newFiles = file.filter(
        (_: any, ind: number) => indexToRemove !== ind
      )
      setFile(newFiles)
    } else if (item?.link) {
      if (needConfirmation === true) {
        setDeleteModal(true)
        setItem(item)
        setAttachmentName?.('')
      } else {
        handleDeleteFile?.(item)
        onChange?.('')
        setFile('')
        setAttachmentName?.('')
      }
    } else {
      onChange?.('')
      setFile('')
      setAttachmentName?.('')
    }
  }
  const handleFileChange = (e: any) => {
    if (e.target.files.length) {
      let isValid = true
      if (supportedFiles?.length) {
        isValid = isValidFile(e?.target?.files[0].type, supportedExtensions)
      } else {
        isValid = true
      }
      if (isValid) {
        if (isMultiple) {
          const files = e.target.files
          const existingFiles = file ?? []
          const filesArray = Array.from(files)

          setFile([...existingFiles, ...filesArray])
        } else {
          if (e?.target?.files[0].size) {
            onChange?.(e)
            setFile(e?.target?.files[0])
            setAttachmentName?.(e?.target?.files[0]?.name)
          } else {
            enqueueSnackbar('Maximum file size 5mb', { variant: 'error' })
            setFile('')
          }
        }
      } else {
        enqueueSnackbar('Invalid file type', { variant: 'error' })
      }
    }
  }
  useEffect(() => {
    if (isMultiple) {
      onChange?.(file)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])
  const handleDeleteConfirmation = () => {
    handleDeleteFile?.(item)
    setFile('')
    setDeleteModal(false)
  }

  // Keep local state in sync when parent updates `value` (e.g., after hydrating from server)
  useEffect(() => {
    setFile(value)
  }, [value])

  // Decide button label: when a file is already selected/prefilled, show "Change image"
  const hasExistingFile = Array.isArray(file) ? file.length > 0 : !!file
  const computedButtonLabel = hasExistingFile
    ? 'Change image'
    : buttonLabel || 'Browse & Upload'

  return (
    <>
      <DialogModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        title={'Are you sure?'}
        onSubmit={() => handleDeleteConfirmation()}
        secondaryAction={() => setDeleteModal(false)}
        secondaryActionLabel="No, Cancel"
        actionLabel="Yes, I am"
        body={
          <InfoBox
            content={
              'Deleting this item is an irreversible action. Are you sure you want to proceed with the deletion?'
            }
          />
        }
      />
      <div className={`customFileUpload ${fullwidth ? 'w-full' : 'w-auto'}`}>
        {label && (
          <div className="">
            <label className={`labels label-text`}>
              {label}
              {required ? <span className="text-error"> *</span> : <></>}
            </label>
          </div>
        )}
        <div
          className={`customFileUpload-field relative flex flex-col items-center  border-dashed border border-formBorder rounded-lg ${disabled ? 'bg-cardWrapperBg' : 'bg-bgGrey'}`}
        >
          <input
            id={id}
            disabled={disabled}
            multiple={isMultiple}
            onChange={handleFileChange}
            type={type}
            accept={accept}
          />
          <label
            className={`flex flex-col items-center justify-center gap-2  p-4 w-full min-h-[120px] ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} `}
            htmlFor={id}
          >
            <p
              className={` text-xxs leading-4  ${disabled ? 'text-input-disabled' : 'text-primaryText'}`}
            >
              {supportedFiles}
              {sizeLimit && (
                <span
                  className={`text-primary text-xxs leading-4 ${disabled && 'opacity-60'}`}
                >
                  (Max {sizeLimit} MB)
                </span>
              )}
            </p>
            <div
              className={`relative   rounded-[4px] shadow-buttonShadow   btn text-sm p-1.5 min-w-[65px] btn-primary ${disabled && 'opacity-30'}`}
            >
              <div className="flex items-center justify-center gap-1  m-auto ">
                <Icons className="iconWhite" name={`${iconName}`} />
                <div className="  font-medium  text-bgWhite text-xxs ">
                  {computedButtonLabel}
                </div>
              </div>
            </div>
          </label>
        </div>
        {errors && errors[name] && (
          <div className="text-error text-error-label mt-[1px]">
            {getErrors(errors[name])}
          </div>
        )}

        {(Array.isArray(file) && file.length > 0) ||
        (!Array.isArray(file) && file) ? (
          <div className="flex flex-col gap-2 mt-4">
            {Array.isArray(file) &&
              file.map((item, index: number) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-1.5 px-2.5 py-2 bg-cardWrapperBg rounded-sm"
                >
                  <Icons name="paper-clip" className="iconWidthSm iconBlack" />
                  <p className="flex-1 text-primaryText text-sm font-medium break-all">
                    {item?.name}
                  </p>
                  {!disabled && (
                    <Icons
                      onClick={() => handleClearFile(index)}
                      name="close"
                      className="iconBlack iconWidthSm cursor-pointer"
                    />
                  )}
                </div>
              ))}
            {typeof file === 'object' && file && !isMultiple && (
              <div className="flex items-center justify-between gap-1.5 px-2.5 py-2 bg-cardWrapperBg rounded-sm">
                <Icons name="paper-clip" className="iconWidthSm" />
                <p className="flex-1 text-primaryText text-sm font-medium break-all">
                  {file?.name}
                </p>
                {!disabled && (
                  <Icons
                    name="close"
                    onClick={() => handleClearFile(0, file)}
                    className="iconBlack iconWidthSm cursor-pointer"
                  />
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </>
  )
}

export default FileUpload
